import React from "react";

/* ---------------------------------------------------------------- */
/* Concept A — "Sankofa Crest"                                       */
/* Heraldic round emblem · ankara-bordered ring · interlocked AM     */
/* monogram in cream + ochre · feels editorial / premium / heritage  */
/* ---------------------------------------------------------------- */
export const LogoCrest = ({ size = 64 }) => {
  const id = React.useId();
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="AfriMarket — Sankofa Crest">
      <defs>
        <radialGradient id={`crest-bg-${id}`} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#1F2A33"/>
          <stop offset="100%" stopColor="#0E141A"/>
        </radialGradient>
        <linearGradient id={`crest-gold-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F2C36B"/>
          <stop offset="60%" stopColor="#D9923B"/>
          <stop offset="100%" stopColor="#A06A20"/>
        </linearGradient>
        <pattern id={`crest-ankara-${id}`} patternUnits="userSpaceOnUse" width="4.5" height="4.5" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="4.5" stroke="#D9923B" strokeWidth="0.4" opacity="0.55"/>
        </pattern>
      </defs>
      {/* Outer ankara ring */}
      <circle cx="48" cy="48" r="46" fill={`url(#crest-ankara-${id})`}/>
      <circle cx="48" cy="48" r="46" fill="none" stroke={`url(#crest-gold-${id})`} strokeWidth="1.4"/>
      <circle cx="48" cy="48" r="41" fill={`url(#crest-bg-${id})`}/>
      {/* Inner gold rule */}
      <circle cx="48" cy="48" r="38" fill="none" stroke={`url(#crest-gold-${id})`} strokeWidth="0.7" opacity="0.65"/>
      {/* Monogram — A overlapping M, rendered as geometric strokes */}
      {/* Left "A" leg */}
      <path d="M28 70 L42 26" stroke="#FAF8F5" strokeWidth="3.2" strokeLinecap="round" fill="none"/>
      <path d="M50 26 L36 70" stroke="#FAF8F5" strokeWidth="3.2" strokeLinecap="round" fill="none"/>
      {/* A crossbar in gold */}
      <line x1="33.5" y1="52" x2="44.5" y2="52" stroke={`url(#crest-gold-${id})`} strokeWidth="2.4" strokeLinecap="round"/>
      {/* Stylised M — three peaks descending right */}
      <path d="M48 70 L52 38 L58 56 L64 38 L68 70"
            stroke={`url(#crest-gold-${id})`} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Apex constellation dot — the diaspora star */}
      <circle cx="48" cy="14" r="2.2" fill={`url(#crest-gold-${id})`}/>
      <circle cx="48" cy="14" r="3.6" fill="none" stroke={`url(#crest-gold-${id})`} strokeWidth="0.5" opacity="0.55"/>
    </svg>
  );
};

/* ---------------------------------------------------------------- */
/* Concept B — "Sun & Continent"                                    */
/* Rising-sun rays in kente colours · continent silhouette in dark  */
/* relief · symbolic, modern, very recognisable at favicon size      */
/* ---------------------------------------------------------------- */
export const LogoSun = ({ size = 64 }) => {
  const id = React.useId();
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="AfriMarket — Sun & Continent">
      <defs>
        <linearGradient id={`sun-bg-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F6E8D6"/>
          <stop offset="100%" stopColor="#EBD2B0"/>
        </linearGradient>
        <linearGradient id={`sun-disc-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F2C36B"/>
          <stop offset="100%" stopColor="#D9923B"/>
        </linearGradient>
      </defs>
      <rect width="96" height="96" rx="20" fill={`url(#sun-bg-${id})`}/>
      {/* Rays — alternating terracotta/ochre/forest, kente strip vibe */}
      <g transform="translate(48 48)">
        {Array.from({ length: 16 }).map((_, i) => {
          const colours = ["#C05A3C", "#D9923B", "#2A3D36"];
          const angle = (i * 360) / 16;
          return (
            <rect key={i}
                  x="-1.4" y="-44" width="2.8" height="14"
                  rx="1.2"
                  fill={colours[i % 3]}
                  transform={`rotate(${angle})`}
                  opacity={i % 2 === 0 ? 0.95 : 0.6}/>
          );
        })}
        {/* Inner disc */}
        <circle r="22" fill={`url(#sun-disc-${id})`}/>
        {/* Stylised African continent silhouette inside the disc */}
        <path d="M-6 -16 C-2 -18 5 -17 7 -13 C10 -7 13 -3 11 4 C9 9 12 12 8 16 C4 19 0 18 -3 14 C-6 11 -10 9 -10 4 C-11 -1 -10 -8 -8 -12 C-8 -14 -7 -15 -6 -16 Z"
              fill="#1A2024" opacity="0.92"/>
        {/* Diaspora dots — small connecting markers around the continent */}
        <circle cx="-14" cy="-2" r="1.1" fill="#FAF8F5"/>
        <circle cx="14" cy="-6" r="1.1" fill="#FAF8F5"/>
        <circle cx="0" cy="22" r="1.1" fill="#FAF8F5"/>
      </g>
    </svg>
  );
};

/* ---------------------------------------------------------------- */
/* Concept C — "Adinkra Knot"                                       */
/* Inspired by NYANSAPO (wisdom knot) — interlaced infinity loop    */
/* in gold on midnight, with a soft ankara halo. Very symbolic.      */
/* ---------------------------------------------------------------- */
export const LogoKnot = ({ size = 64 }) => {
  const id = React.useId();
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="AfriMarket — Adinkra Knot">
      <defs>
        <linearGradient id={`knot-bg-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#22343C"/>
          <stop offset="100%" stopColor="#0F1A20"/>
        </linearGradient>
        <linearGradient id={`knot-gold-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F4D17A"/>
          <stop offset="50%" stopColor="#D9923B"/>
          <stop offset="100%" stopColor="#A06A20"/>
        </linearGradient>
      </defs>
      <rect width="96" height="96" rx="20" fill={`url(#knot-bg-${id})`}/>
      {/* Soft halo */}
      <circle cx="48" cy="48" r="36" fill="none" stroke="#D9923B" strokeWidth="0.5" opacity="0.35"/>
      <circle cx="48" cy="48" r="32" fill="none" stroke="#D9923B" strokeWidth="0.5" opacity="0.18"/>
      {/* The interlaced wisdom-knot — two overlapping loops with crossings */}
      <g stroke={`url(#knot-gold-${id})`} strokeWidth="3.6" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M48 18 C68 18 78 38 68 48 C58 58 38 58 28 48 C18 38 28 18 48 18 Z"/>
        <path d="M48 78 C28 78 18 58 28 48 C38 38 58 38 68 48 C78 58 68 78 48 78 Z"/>
      </g>
      {/* Crossing accents — small gold dots at the four intersection points */}
      <circle cx="32.5" cy="44" r="2" fill="#FAF8F5"/>
      <circle cx="63.5" cy="44" r="2" fill="#FAF8F5"/>
      <circle cx="32.5" cy="52" r="2" fill="#FAF8F5"/>
      <circle cx="63.5" cy="52" r="2" fill="#FAF8F5"/>
      {/* Centre A monogram in soft cream */}
      <text x="48" y="55" textAnchor="middle"
            fontFamily="'Cormorant Garamond', Georgia, serif"
            fontStyle="italic"
            fontWeight="600"
            fontSize="22"
            fill="#FAF8F5">A</text>
    </svg>
  );
};

const LogoCandidates = () => (
  <div className="min-h-screen bg-[#FAF8F5] py-16">
    <div className="container-narrow text-center">
      <div className="label-overline text-[#C05A3C] mb-2">Brand mark exploration</div>
      <h1 className="font-display text-5xl mb-3">Pick your AfriMarket logo</h1>
      <p className="text-muted-foreground mb-12 max-w-xl mx-auto">Three elegant directions, all designed to feel premium, distinctly African, and to read clearly on both desktop and mobile.</p>
      <div className="grid md:grid-cols-3 gap-10">
        {[
          { name: "A · Sankofa Crest", node: <LogoCrest size={160}/>, desc: "Heraldic crest · interlocked AM monogram · ankara ring · feels like a heritage editorial brand." },
          { name: "B · Sun & Continent", node: <LogoSun size={160}/>, desc: "Rising kente sun · continent silhouette · diaspora dots — story-driven, instantly readable." },
          { name: "C · Adinkra Knot", node: <LogoKnot size={160}/>, desc: "Nyansapo wisdom-knot · gold on midnight · italic A · symbolic, luxe, distinctive." },
        ].map((c) => (
          <div key={c.name} className="bg-white border border-black/10 rounded-2xl p-8 shadow-sm">
            <div className="flex justify-center mb-6">{c.node}</div>
            <div className="flex items-center gap-3 justify-center mb-3">
              {c.name === "A · Sankofa Crest" ? <LogoCrest size={32}/>
                : c.name === "B · Sun & Continent" ? <LogoSun size={32}/>
                : <LogoKnot size={32}/>}
              <span className="font-display font-semibold text-2xl tracking-tight">
                Afri<span className="relative">M<span className="absolute -top-[2px] right-[4px] w-[3px] h-[3px] bg-[#D9923B] rounded-[1px]" aria-hidden="true"/></span>arket
              </span>
            </div>
            <h3 className="font-display text-xl mb-2">{c.name}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default LogoCandidates;
